/**
 * Firebase Authentication Service
 * Handles phone number authentication with Firebase
 */
import { initializeApp } from 'firebase/app';
import {
  getAuth,
  setPersistence,
  browserLocalPersistence,
  RecaptchaVerifier,
  signInWithPhoneNumber,
  PhoneAuthProvider,
  signInWithCredential,
  onAuthStateChanged,
} from 'firebase/auth';

const firebaseConfig = {
  apiKey: "AIzaSyAWQhhp49ta3DCWUodIXjczY6f23Pwc4H8",
  authDomain: "ollie-d5b80.firebaseapp.com",
  projectId: "ollie-d5b80",
  storageBucket: "ollie-d5b80.firebasestorage.app",
  messagingSenderId: "641855909307",
  appId: "1:641855909307:web:b9cd40615b5523c772689e",
  measurementId: "G-9J9YJL246E"
};

const app = initializeApp(firebaseConfig);
const auth = getAuth(app);

// Enable persistence for long-lived sessions (keeps user logged in across browser sessions)
// This allows users to stay authenticated for up to 1 week without re-entering phone number
setPersistence(auth, browserLocalPersistence).catch((error) => {
  console.error('[Firebase] Failed to set persistence:', error);
});

class FirebaseService {
  constructor() {
    this.auth = auth;
    this.confirmationResult = null;
    this.recaptchaVerifier = null;
  }

  /**
   * Initialize reCAPTCHA verifier
   * @param {string} containerId - ID of the container element for reCAPTCHA
   */
  initRecaptcha(containerId = 'recaptcha-container') {
    if (this.recaptchaVerifier) {
      return this.recaptchaVerifier;
    }

    this.recaptchaVerifier = new RecaptchaVerifier(auth, containerId, {
      size: 'invisible',
      callback: (response) => {
        console.log('reCAPTCHA solved');
      },
      'expired-callback': () => {
        console.log('reCAPTCHA expired');
      },
    });

    return this.recaptchaVerifier;
  }

  /**
   * Send SMS verification code to phone number
   * @param {string} phoneNumber - Phone number in E.164 format (e.g., +971501234567)
   * @returns {Promise<void>}
   */
  async sendVerificationCode(phoneNumber) {
    try {
      // Validate phone number format
      if (!phoneNumber.startsWith('+')) {
        throw new Error('Phone number must be in E.164 format (e.g., +971501234567)');
      }

      // Initialize reCAPTCHA if not already done
      if (!this.recaptchaVerifier) {
        this.initRecaptcha();
      }

      // Send SMS
      this.confirmationResult = await signInWithPhoneNumber(
        this.auth,
        phoneNumber,
        this.recaptchaVerifier
      );

      console.log('SMS sent successfully');
      return { success: true };
    } catch (error) {
      console.error('Error sending SMS:', error);
      throw this.handleError(error);
    }
  }

  /**
   * Verify the SMS code entered by user
   * @param {string} code - 6-digit verification code
   * @returns {Promise<{phoneNumber: string, idToken: string, success: boolean}>}
   */
  async verifyCode(code) {
    try {
      if (!this.confirmationResult) {
        throw new Error('No confirmation result found. Please request a new code.');
      }

      const result = await this.confirmationResult.confirm(code);
      const user = result.user;

      // Get ID token for backend verification
      const idToken = await user.getIdToken();

      console.log('Phone number verified successfully:', user.phoneNumber);

      return {
        success: true,
        phoneNumber: user.phoneNumber,
        uid: user.uid,
        idToken: idToken, // Backend will verify this token
      };
    } catch (error) {
      console.error('Error verifying code:', error);
      throw this.handleError(error);
    }
  }

  /**
   * Get the current authenticated user (full Firebase user object)
   * @returns {Promise<User | null>}
   */
  async getCurrentUser() {
    return new Promise((resolve) => {
      const unsubscribe = onAuthStateChanged(this.auth, (user) => {
        unsubscribe();
        resolve(user);
      });
    });
  }

  /**
   * Get Firebase ID token for authenticated user
   * @returns {Promise<string>}
   */
  async getIdToken() {
    const user = await this.getCurrentUser();
    if (!user) {
      throw new Error('No authenticated user');
    }
    return await user.getIdToken();
  }

  /**
   * Sign out the current user
   */
  async signOut() {
    try {
      await this.auth.signOut();
      return true;
    } catch (error) {
      console.error('[Firebase] Error signing out:', error);
      throw this.handleError(error);
    }
  }

  /**
   * Reset reCAPTCHA verifier (useful when retrying)
   */
  resetRecaptcha() {
    if (this.recaptchaVerifier) {
      this.recaptchaVerifier.clear();
      this.recaptchaVerifier = null;
    }
    this.confirmationResult = null;
  }

  /**
   * Handle Firebase errors and return user-friendly messages
   */
  handleError(error) {
    let message = 'An error occurred. Please try again.';

    switch (error.code) {
      case 'auth/invalid-phone-number':
        message = 'Invalid phone number format. Please use international format (e.g., +971501234567)';
        break;
      case 'auth/missing-phone-number':
        message = 'Please enter a phone number';
        break;
      case 'auth/invalid-verification-code':
        message = 'Invalid verification code. Please check and try again.';
        break;
      case 'auth/code-expired':
        message = 'Verification code has expired. Please request a new one.';
        break;
      case 'auth/too-many-requests':
        message = 'Too many attempts. Please try again later.';
        break;
      case 'auth/captcha-check-failed':
        message = 'reCAPTCHA verification failed. Please try again.';
        break;
      default:
        message = error.message || message;
    }

    return new Error(message);
  }
}

// Export a singleton instance
export const firebaseService = new FirebaseService();
export default firebaseService;

